/**
 * Customer lookup -- phase 6b, Task 4.
 *
 * Reuses the SAME search request as the existing Customers page
 * (`src/components/UserManagement/CustomersTab.tsx`): `GET
 * admin/users/customers/search?search=<term>` over the shared `authInstance`.
 * This is a general user-directory endpoint, not a discounts-area route, so
 * it is deliberately not added to `discountsApi` (task-4 brief: "reuse the
 * existing customer search... do not invent an endpoint").
 *
 * Selecting a result -- or pasting a customer (user) id directly, which skips
 * the search call entirely -- navigates to `/discounts/customers/:id`.
 *
 * The search endpoint's controller runs `assertAdminAccess` (owner/admin),
 * so the search box is rendered only for `useCanWriteDiscounts()`; every
 * other role gets the paste-an-id field alone (fix wave I1) -- the same
 * navigation, no request that would 403.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authInstance } from '../../../services/httpClient';
import { toApiFailure } from '../errors';
import { CUSTOMER_SEARCH_RESTRICTED_HINT, useCanWriteDiscounts } from '../roles';

type SearchCustomer = {
  id: string;
  email: string;
  firstName: string;
  lastName: string | null;
  phoneNumber: string | null;
};

/** Matches the same shape the app assigns to user ids (uuid v1-v5, case-insensitive). */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const CustomerLookup: React.FC = () => {
  const navigate = useNavigate();
  const canSearch = useCanWriteDiscounts();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<SearchCustomer[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openCustomer = (id: string) => navigate(`/discounts/customers/${id}`);

  const isId = UUID_RE.test(term.trim());

  const runSearch = async () => {
    const value = term.trim();
    if (!value) return;

    if (UUID_RE.test(value)) {
      openCustomer(value);
      return;
    }
    if (!canSearch) {
      setError(`That is not a customer id. ${CUSTOMER_SEARCH_RESTRICTED_HINT}`);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await authInstance.get('admin/users/customers/search', { params: { search: value } });
      setResults((res.data?.customers ?? []) as SearchCustomer[]);
    } catch (err) {
      // The one mapper every discounts screen uses (errors.ts): userMessage /
      // message when the API sent one, otherwise the fixed copy per status.
      setError(toApiFailure(err).message);
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        Find a customer
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {canSearch
          ? 'Search by name, email, phone or order number — or paste a customer id to open it directly.'
          : CUSTOMER_SEARCH_RESTRICTED_HINT}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label={canSearch ? 'Search or paste a customer id' : 'Paste a customer id'}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && runSearch()}
          slotProps={{
            input: {
              startAdornment: canSearch ? (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
        <Button
          variant="contained"
          onClick={runSearch}
          disabled={loading || !term.trim()}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {isId || !canSearch ? 'Open' : 'Search'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {results && results.length === 0 && !loading && (
        <Typography color="text.secondary">No customers found.</Typography>
      )}

      {results && results.length > 0 && (
        <List sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {results.map((c) => (
            <ListItemButton key={c.id} onClick={() => openCustomer(c.id)} divider>
              <ListItemText
                primary={`${c.firstName} ${c.lastName ?? ''}`.trim()}
                secondary={[c.email, c.phoneNumber].filter(Boolean).join(' · ')}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );
};

export default CustomerLookup;
